const JOIN_ERROR_MESSAGES = {
  appointment_not_found: 'No matching appointment was found for this video session.',
  appointment_not_started: 'This visit has not started yet. You can join shortly before the scheduled time.',
  appointment_ended: 'This visit window has ended or the session was closed.',
  appointment_cancelled: 'This appointment was cancelled or declined.',
  appointment_not_confirmed: 'This appointment is still a pending request and cannot be joined yet.',
  wrong_patient: 'This session is scheduled for a different patient.',
  not_assigned_provider: 'You are not the assigned clinician for this appointment.',
  session_in_use: 'Another patient is already in this call.',
  not_authorized_for_appointment: 'You are not authorized to join this appointment.',
  scheduling_conflict: 'That time conflicts with another appointment.',
};

export const APPOINTMENT_TITLE_MAX_LENGTH = 50;
export const APPOINTMENT_NOTES_MAX_LENGTH = 1000;
export const APPOINTMENT_TITLE_CUSTOM = '__custom__';
export const APPOINTMENT_TITLE_PRESETS = [
  'New Consultation',
  'Regular Checkup',
  'Follow-Up',
  'Emergency Visit',
];

export function resolveAppointmentTitleMode(title) {
  const cleaned = String(title || '').trim();
  if (!cleaned) {
    return { mode: APPOINTMENT_TITLE_PRESETS[0], customTitle: '' };
  }
  if (APPOINTMENT_TITLE_PRESETS.includes(cleaned)) {
    return { mode: cleaned, customTitle: '' };
  }
  return {
    mode: APPOINTMENT_TITLE_CUSTOM,
    customTitle: cleaned.slice(0, APPOINTMENT_TITLE_MAX_LENGTH),
  };
}

export function zoomJoinErrorMessage(detailOrCode) {
  if (!detailOrCode) return 'Unable to join this video session.';
  const code = String(detailOrCode).trim();
  if (JOIN_ERROR_MESSAGES[code]) return JOIN_ERROR_MESSAGES[code];
  return code;
}

export const APPOINTMENT_PUSH_TYPES = Object.freeze([
  'appointment_request',
  'appointment_accepted',
  'appointment_declined',
  'appointment_reminder',
  'appointment_scheduled',
  'appointment_cancelled',
  'appointment_deleted',
]);

export function isAppointmentPushType(typeOrItem) {
  const type =
    typeof typeOrItem === 'string'
      ? typeOrItem
      : typeOrItem?.notificationType || typeOrItem?.type || '';
  const t = String(type || '').toLowerCase();
  return APPOINTMENT_PUSH_TYPES.includes(t);
}

export function resolveAppointmentNotificationUrl(typeOrItem, appointmentId) {
  const type =
    typeof typeOrItem === 'string'
      ? typeOrItem
      : typeOrItem?.notificationType || typeOrItem?.type || '';
  const t = String(type || '').toLowerCase();
  const id = appointmentId || typeOrItem?.appointmentId || typeOrItem?.appointment_id;
  if (!APPOINTMENT_PUSH_TYPES.includes(t)) return null;
  if (!id) return '/appointments';
  if (t === 'appointment_request') return `/appointments?pendingRequest=${id}`;
  return `/appointments?appointmentId=${id}`;
}

export function isZoomPushType(typeOrItem) {
  const type =
    typeof typeOrItem === 'string'
      ? typeOrItem
      : typeOrItem?.notificationType || typeOrItem?.type || '';
  const t = String(type || '').toLowerCase();
  return (
    t === 'video_waiting_room' ||
    t === 'video_patient_left_call' ||
    t.startsWith('zoom_')
  );
}

export function isAppointmentsRelatedPush(typeOrItem) {
  return isZoomPushType(typeOrItem) || isAppointmentPushType(typeOrItem);
}

export function appointmentEffectiveEnd(appointment, now = Date.now()) {
  if (!appointment) return null;
  if (appointment.endTime) {
    const end = new Date(appointment.endTime);
    return Number.isNaN(end.getTime()) ? null : end;
  }
  if (!appointment.startTime) return null;
  const start = new Date(appointment.startTime);
  if (Number.isNaN(start.getTime())) return null;
  const mins = Number(appointment.duration) > 0 ? Number(appointment.duration) : 30;
  return new Date(start.getTime() + mins * 60 * 1000);
}

/** Match backend appointment_time_bucket: upcoming | past */
export function appointmentTimeBucket(appointment, nowMs = Date.now()) {
  const status = String(appointment?.status || '').trim();
  const conf = String(appointment?.confirmation_status || '').trim();
  if (
    status === 'Completed' ||
    status === 'Cancelled' ||
    status === 'No-Show' ||
    status === 'No-show' ||
    conf === 'Declined' ||
    conf === 'Cancelled'
  ) {
    return 'past';
  }
  if (status === 'Requested') return 'upcoming';
  const end = appointmentEffectiveEnd(appointment);
  if (end && end.getTime() < nowMs) return 'past';
  return 'upcoming';
}

export function toDatetimeLocalValue(isoOrDate) {
  if (!isoOrDate) return '';
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

export function fromDatetimeLocalValue(localValue) {
  if (!localValue) return null;
  const raw = String(localValue).trim();
  const match = raw.match(
    /^(\d{4}-\d{2}-\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (!match) return null;
  const [, datePart, hh, mm, ss] = match;
  return `${datePart}T${hh}:${mm}:${ss || '00'}`;
}

export function defaultDurationMinutes(appointment) {
  if (appointment?.duration && Number(appointment.duration) > 0) {
    return Number(appointment.duration);
  }
  if (appointment?.startTime && appointment?.endTime) {
    const start = new Date(appointment.startTime).getTime();
    const end = new Date(appointment.endTime).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      return Math.max(5, Math.round((end - start) / 60000));
    }
  }
  return 30;
}

export function addMinutesToDatetimeLocal(localValue, minutes) {
  if (!localValue) return '';
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return '';
  d.setMinutes(d.getMinutes() + Number(minutes || 30));
  return toDatetimeLocalValue(d);
}

export function computeEndFromStartLocal(startLocal, durationMinutes = 30) {
  return addMinutesToDatetimeLocal(startLocal, durationMinutes);
}

export function appointmentAuthHeaders(token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}
